import type { AlbumDocument } from "../lib/types.ts";
import * as db from "../storage/indexeddb.ts";
import * as opfs from "../storage/opfs.ts";
import { router } from "../router.ts";
import { store } from "../store.ts";
import {
  isPortraitOrientation,
  renderPage as renderAlbumPage,
  spreadIndices,
} from "./page-render.ts";
import { animateAlbumPageTurn, type PreparedPageTurn } from "./page-turn.ts";

function ghostButton(action: string, label: string, icon: string, className = ""): HTMLButtonElement {
  const btn = document.createElement("button");
  btn.type = "button";
  btn.className = `ghost-icon${className ? ` ${className}` : ""}`;
  btn.dataset.action = action;
  btn.setAttribute("aria-label", label);
  btn.title = label;
  btn.textContent = icon;
  return btn;
}

function pageIndicatorText(album: AlbumDocument, leftIndex: number): string {
  const rightIndex = leftIndex + 1;
  if (album.pages[rightIndex]) {
    return `${leftIndex + 1}-${rightIndex + 1} / ${album.pages.length}`;
  }
  return `${leftIndex + 1} / ${album.pages.length}`;
}

export async function renderViewer(
  root: HTMLElement,
  albumId: string,
  pageIndex: number,
  signal: AbortSignal,
): Promise<() => void> {
  const initial = await db.getAlbum(albumId);
  if (signal.aborted) return () => {};
  if (!initial) {
    const err = document.createElement("p");
    err.className = "status error";
    err.textContent = "Album not found.";
    root.replaceChildren(err);
    return () => {};
  }

  let currentPage = Math.max(0, Math.min(pageIndex, initial.pages.length - 1));
  let animating = false;
  let paintGeneration = 0;
  const motionQuery = window.matchMedia("(prefers-reduced-motion: reduce)");

  const revokedPaths: string[] = [];

  async function paint(
    prepared?: PreparedPageTurn,
    beforeSwap?: () => void,
  ): Promise<boolean> {
    if (signal.aborted) return false;
    const generation = ++paintGeneration;
    const pageAtPaint = currentPage;
    const album = await db.getAlbum(albumId);
    if (signal.aborted || generation !== paintGeneration || pageAtPaint !== currentPage) {
      return false;
    }
    if (!album) {
      const err = document.createElement("p");
      err.className = "status error";
      err.textContent = "Album not found.";
      root.replaceChildren(err);
      return true;
    }

    const view = document.createElement("div");
    view.className = "overlay-view viewer";

    const chrome = document.createElement("div");
    chrome.className = "overlay-chrome";

    const backBtn = ghostButton("back", "Back", "←");
    backBtn.classList.add("overlay-chrome-back");

    const title = document.createElement("h1");
    title.className = "overlay-title";
    title.textContent = album.title;

    const actions = document.createElement("div");
    actions.className = "overlay-chrome-actions";
    const editBtn = ghostButton("edit", "Edit", "✎");
    actions.append(editBtn);

    chrome.append(backBtn, title, actions);

    const { left: leftIndex, right: rightIndex } = spreadIndices(pageAtPaint);

    const spread = document.createElement("div");
    spread.className = "album-spread viewer";
    spread.setAttribute("role", "region");
    spread.setAttribute("aria-label", "Album pages");

    let leftPage: HTMLElement;
    let rightPage: HTMLElement;
    if (
      prepared &&
      prepared.pageIndex === pageAtPaint &&
      prepared.albumRevision === album.revision
    ) {
      leftPage = prepared.leftPage;
      rightPage = prepared.rightPage;
    } else {
      leftPage = document.createElement("div");
      leftPage.className = "album-page left";
      rightPage = document.createElement("div");
      rightPage.className = "album-page right";
      await Promise.all([
        renderAlbumPage(leftPage, album, leftIndex, false),
        renderAlbumPage(rightPage, album, rightIndex, false),
      ]);
    }

    if (signal.aborted || generation !== paintGeneration || pageAtPaint !== currentPage) {
      return false;
    }

    spread.append(leftPage, rightPage);

    const prevBtn = ghostButton("prev", "Previous page", "‹", "spread-edge-nav prev");
    const nextBtn = ghostButton("next", "Next page", "›", "spread-edge-nav next");
    prevBtn.disabled = leftIndex - 2 < 0;
    nextBtn.disabled = leftIndex + 2 >= album.pages.length;
    spread.append(prevBtn, nextBtn);

    const indicator = document.createElement("span");
    indicator.className = "page-indicator";
    indicator.textContent = pageIndicatorText(album, leftIndex);

    view.append(chrome, spread, indicator);
    // Swap in one step: an emptied root between frames reads as a flash.
    if (signal.aborted || generation !== paintGeneration || pageAtPaint !== currentPage) {
      return false;
    }
    beforeSwap?.();
    root.replaceChildren(view);

    backBtn.addEventListener("click", () => router.navigate({ name: "shelf" }));
    editBtn.addEventListener("click", () =>
      router.navigate({ name: "editor", albumId, page: currentPage }),
    );

    const restoreControlFocus = (action: string) => {
      let target = root.querySelector<HTMLButtonElement>(`[data-action="${action}"]`);
      if (target?.disabled && (action === "prev" || action === "next")) {
        const fallbackAction = action === "next" ? "prev" : "next";
        target = root.querySelector<HTMLButtonElement>(`[data-action="${fallbackAction}"]`);
      }
      target?.focus();
    };

    const go = async (delta: number) => {
      if (animating || signal.aborted) return;
      const spreadStart = spreadIndices(currentPage).left;
      const next = spreadStart + delta * 2;
      if (next < 0 || next >= album.pages.length) return;
      animating = true;
      spread.setAttribute("aria-busy", "true");
      const activeAction =
        document.activeElement instanceof HTMLElement
          ? document.activeElement.dataset.action
          : undefined;
      const focusedAction =
        activeAction === "prev" ||
        activeAction === "next" ||
        activeAction === "back" ||
        activeAction === "edit"
          ? activeAction
          : null;
      const prevWasDisabled = prevBtn.disabled;
      const nextWasDisabled = nextBtn.disabled;
      prevBtn.disabled = true;
      nextBtn.disabled = true;
      const previousPage = currentPage;
      const commit = async (turnPages?: PreparedPageTurn) => {
        if (signal.aborted || !spread.isConnected) return;
        currentPage = next;
        try {
          const painted = await paint(turnPages, () =>
            router.navigate({ name: "viewer", albumId, page: currentPage }, true, true),
          );
          if (!painted || signal.aborted) {
            if (!signal.aborted) currentPage = previousPage;
            return;
          }
          if (focusedAction) restoreControlFocus(focusedAction);
        } catch (error) {
          currentPage = previousPage;
          throw error;
        }
      };
      try {
        if (store.settings?.reducedMotion || motionQuery.matches) {
          await commit();
        } else {
          await animateAlbumPageTurn({
            spread,
            leftPage,
            rightPage,
            album,
            currentPage: spreadStart,
            forward: delta > 0,
            signal,
            motionQuery,
            commit,
          });
        }
      } catch (error) {
        if (!signal.aborted) console.error("Failed to turn album page", error);
      } finally {
        animating = false;
        if (spread.isConnected) {
          spread.removeAttribute("aria-busy");
          prevBtn.disabled = prevWasDisabled;
          nextBtn.disabled = nextWasDisabled;
          if (focusedAction) restoreControlFocus(focusedAction);
        }
      }
    };

    prevBtn.addEventListener("click", (e) => {
      e.stopPropagation();
      void go(-1);
    });
    nextBtn.addEventListener("click", (e) => {
      e.stopPropagation();
      void go(1);
    });

    spread.addEventListener("click", (e) => {
      if ((e.target as HTMLElement).closest(".ghost-icon")) return;
      const rect = spread.getBoundingClientRect();
      const x = (e as MouseEvent).clientX - rect.left;
      const y = (e as MouseEvent).clientY - rect.top;
      if (isPortraitOrientation()) {
        if (y < rect.height * 0.3) void go(-1);
        else if (y > rect.height * 0.7) void go(1);
      } else {
        if (x < rect.width * 0.3) void go(-1);
        else if (x > rect.width * 0.7) void go(1);
      }
    });

    let touchStartX = 0;
    let touchStartY = 0;
    spread.addEventListener(
      "touchstart",
      (e) => {
        touchStartX = e.touches[0]?.clientX ?? 0;
        touchStartY = e.touches[0]?.clientY ?? 0;
      },
      { passive: true },
    );
    spread.addEventListener(
      "touchend",
      (e) => {
        const dx = (e.changedTouches[0]?.clientX ?? 0) - touchStartX;
        const dy = (e.changedTouches[0]?.clientY ?? 0) - touchStartY;
        if (isPortraitOrientation()) {
          if (Math.abs(dy) > 50 && Math.abs(dy) > Math.abs(dx)) void go(dy < 0 ? 1 : -1);
        } else if (Math.abs(dx) > 50) {
          void go(dx < 0 ? 1 : -1);
        }
      },
      { passive: true },
    );

    return true;
  }

  const onKey = (e: KeyboardEvent) => {
    let action: "prev" | "next" | null = null;
    if (e.key === "ArrowLeft" || (e.key === "ArrowUp" && isPortraitOrientation())) {
      action = "prev";
    } else if (e.key === "ArrowRight" || (e.key === "ArrowDown" && isPortraitOrientation())) {
      action = "next";
    }
    if (!action) return;
    e.preventDefault();
    root.querySelector<HTMLButtonElement>(`[data-action="${action}"]`)?.click();
  };
  window.addEventListener("keydown", onKey);
  await paint();

  return () => {
    paintGeneration++;
    window.removeEventListener("keydown", onKey);
    for (const path of revokedPaths) opfs.revokeObjectUrl(path);
  };
}
